import { useEffect, useState } from 'react'
import type { Canon, Profile } from '../../core/types.ts'
import { deriveCategoryId } from '../../core/categorize.ts'
import { api, type ListSummary } from '../lib/api.ts'

/**
 * Tisková podoba (artboard 06). Dostupná i u zamčeného seznamu bez hesla —
 * čtecí cesty jsou záměrně otevřené. Obálky se netisknou, papír se odevzdává.
 */
export function PrintPage({ listId }: { listId: string }) {
  const [data, setData] = useState<{ list: ListSummary; canon: Canon; profile: Profile } | null>(null)

  useEffect(() => {
    void (async () => {
      const list = await api.list(listId)
      const [canon, profiles] = await Promise.all([api.canon(list.canonId), api.profiles()])
      setData({ list, canon, profile: profiles.find((p) => p.id === list.profileId) ?? profiles[0]! })
    })()
  }, [listId])

  if (!data) return <p className="p-8 text-[var(--muted-foreground)]">Načítám…</p>

  const { list, canon, profile } = data
  const selected = canon.works.filter((w) => list.selectedWorkIds.includes(w.id))
  const categories = [...profile.categories].sort((a, b) => a.order - b.order)
  let n = 0

  return (
    <div className="min-h-dvh bg-[var(--background)] py-8 font-serif">
      <div className="mx-auto mb-4 flex max-w-[210mm] flex-wrap items-center justify-between gap-2 px-4 no-print sm:px-6">
        <a href={`/lists/${listId}`} className="font-sans text-[length:var(--text-sm)] underline">← Zpět do editoru</a>
        <button
          type="button"
          onClick={() => window.print()}
          className="rounded-[var(--radius-md)] bg-[var(--primary)] px-4 py-2 font-sans text-[length:var(--text-sm)] text-[var(--primary-foreground)]"
        >
          Vytisknout
        </button>
      </div>

      <article className="print-sheet mx-auto max-w-[210mm] bg-white px-5 py-8 text-[#14110e] shadow-sm sm:px-[20mm] sm:py-[22mm]">
        <header className="mb-8 text-center">
          <h1 className="text-[1.6rem] font-semibold tracking-[var(--tracking-title)]">
            Seznam literárních děl k ústní zkoušce
          </h1>
          <p className="mt-1.5 text-[0.95rem]">{list.studentName ?? list.name}</p>
          <p className="mt-0.5 text-[0.8rem] text-[#4a443c]">{canon.name}</p>
        </header>

        {categories.map((category) => {
          const inCategory = selected.filter((w) => deriveCategoryId(w, profile) === category.id)
          if (inCategory.length === 0) return null
          return (
            <section key={category.id} className="print-group mb-6">
              <h2 className="print-group-heading mb-2 border-b border-[#cfc9bf] pb-1 text-[0.7rem] font-semibold uppercase tracking-[var(--tracking-label)] font-sans">
                {category.label}
              </h2>
              <ol className="space-y-1">
                {inCategory.map((w) => (
                  <li key={w.id} className="print-row flex gap-2 text-[0.95rem] leading-[1.5]">
                    <span className="tabular w-6 shrink-0 text-right text-[#4a443c]">{++n}.</span>
                    <span>
                      {w.author && <span>{w.author}: </span>}
                      <em>{w.title}</em>
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          )
        })}

        {selected.length === 0 && <p className="text-center text-[#4a443c]">Seznam zatím neobsahuje žádná díla.</p>}

        <footer className="mt-10 flex justify-between text-[0.8rem] text-[#4a443c]">
          <span>Celkem {selected.length} děl</span>
          <span>Podpis studenta: ________________________</span>
        </footer>
      </article>
    </div>
  )
}
